/**
 * Pure decision for the BASIC solo catch-up follow. The idle cat trails the
 * active cat on the ground; when it strands (companionWalk is horizontal-only
 * and cannot jump gaps) past a dwell, the caller warps it adjacent to the
 * active cat. Warp is HARD-gated to solo + non-QA so it can never fire in
 * co-op or in real-play QA (which runs co-op navigatePair).
 */
export interface SoloFollowParams {
  soloMode: boolean;
  qaActive: boolean;
  activeX: number;
  idleX: number;
  secondsStranded: number;
  triggerDist: number;
  stopDeadband: number;
  warpDist: number;
  warpDwellSeconds: number;
  following: boolean;
  /**
   * True when the idle cat is parked somewhere deliberate — standing on a
   * pressed pressure plate or inside an open portal's entry zone. A parked
   * cat holds its station: it must not chase the active cat, or every
   * "leave one cat here" mechanic breaks the moment the player walks the
   * other cat more than triggerDist away.
   */
  idleOnStation?: boolean;
}
export interface SoloFollowResult {
  companionWalk: -1 | 0 | 1;
  warp: boolean;
  following: boolean;
}
export function decideSoloFollow(p: SoloFollowParams): SoloFollowResult {
  // Solo-assist is a real-gameplay convenience only. It must be fully inert
  // under the QA navigator (which sets soloMode=true for single-cat navigate)
  // so it can never perturb traversal/arc timing — both the companionWalk
  // follow AND the warp are disabled when qaActive.
  if (!p.soloMode || p.qaActive === true) {
    return { companionWalk: 0, warp: false, following: false };
  }
  // Hold station: never abandon a plate/portal the player parked us on.
  if (p.idleOnStation === true) {
    return { companionWalk: 0, warp: false, following: false };
  }
  const dx = p.activeX - p.idleX;
  const dist = Math.abs(dx);
  // Hysteresis: start following past triggerDist; keep following until within stopDeadband.
  let following = p.following;
  if (dist > p.triggerDist) following = true;
  else if (dist <= p.stopDeadband) following = false;
  const companionWalk: -1 | 0 | 1 = following ? (dx > 0 ? 1 : -1) : 0;
  // soloMode === true && qaActive !== true are already guaranteed by the
  // early return above, so the warp only depends on distance + dwell here.
  const warp = dist > p.warpDist && p.secondsStranded >= p.warpDwellSeconds;
  return { companionWalk, warp, following };
}
